// Admin management utilities
const ADMIN_STORAGE_KEY = 'chess_admin_settings';
const ADMIN_CREDENTIALS_KEY = 'chess_admin_credentials';

// Import role management
import { getUserRole, hasPermission, PERMISSIONS, initializeRoleSystem, setUserRole } from './roleManager';

// Default admin credentials (in production, these should be hashed and stored securely)
const DEFAULT_ADMIN_CREDENTIALS = {
  admin: 'chess123', // username: password
  moderator: 'mod456'
};

// Default admin settings
const DEFAULT_ADMIN_SETTINGS = {
  gameTimeLimit: 120, // seconds
  maxGamesPerDay: 50,
  enableTutorial: true,
  enableLeaderboard: true,
  maintenanceMode: false,
  welcomeMessage: 'Welcome to Chess Master!',
  allowRegistration: true,
  minRatingForLeaderboard: 0,
  maxPlayersOnLeaderboard: 100,
  autoBackupEnabled: true,
  backupInterval: 24, // hours
  lastBackup: null,
  roleBasedAccess: true,
  sessionTimeout: 24, // hours
  createdAt: Date.now(),
  updatedAt: Date.now()
};

// Get admin settings
export const getAdminSettings = () => {
  try {
    const settings = localStorage.getItem(ADMIN_STORAGE_KEY);
    return settings ? { ...DEFAULT_ADMIN_SETTINGS, ...JSON.parse(settings) } : DEFAULT_ADMIN_SETTINGS;
  } catch (error) {
    console.error('Error loading admin settings:', error);
    return DEFAULT_ADMIN_SETTINGS;
  }
};

// Save admin settings
export const saveAdminSettings = (settings) => {
  try {
    const updatedSettings = {
      ...settings,
      updatedAt: Date.now()
    };
    localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(updatedSettings));
    return true;
  } catch (error) {
    console.error('Error saving admin settings:', error);
    return false;
  }
};

// Get admin credentials
export const getAdminCredentials = () => {
  try {
    const credentials = localStorage.getItem(ADMIN_CREDENTIALS_KEY);
    return credentials ? JSON.parse(credentials) : DEFAULT_ADMIN_CREDENTIALS;
  } catch (error) {
    console.error('Error loading admin credentials:', error);
    return DEFAULT_ADMIN_CREDENTIALS;
  }
};

// Save admin credentials
export const saveAdminCredentials = (credentials) => {
  try {
    localStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(credentials));
    return true;
  } catch (error) {
    console.error('Error saving admin credentials:', error);
    return false;
  }
};

// Verify admin credentials with role checking
export const verifyAdminCredentials = async (username, password) => {
  try {
    // Simulate async authentication
    await new Promise(resolve => setTimeout(resolve, 500));

    const credentials = getAdminCredentials();
    const isValidCredential = credentials[username] === password;

    if (!isValidCredential) {
      console.log('Invalid credentials for user:', username);
      return false;
    }

    // Ensure the user has proper admin role
    const userRole = getUserRole(username);
    console.log('User role for', username, ':', userRole);

    // Check if user has admin permissions
    const hasAdminAccess = hasPermission(username, PERMISSIONS.USER_MANAGEMENT) || 
                          hasPermission(username, PERMISSIONS.SYSTEM_SETTINGS) || 
                          hasPermission(username, PERMISSIONS.VIEW_ANALYTICS);

    console.log('User has admin access:', hasAdminAccess);
    return hasAdminAccess;
  } catch (error) {
    console.error('Error verifying admin credentials:', error);
    return false;
  }
};

// Check if user is admin with role-based checking
export const isAdminAuthenticated = () => {
  const isAuthenticated = sessionStorage.getItem('admin_authenticated') === 'true';
  if (!isAuthenticated) return false;

  const adminUser = sessionStorage.getItem('admin_user');
  if (!adminUser) return false;

  // Check if user still has admin permissions
  return hasPermission(adminUser, PERMISSIONS.USER_MANAGEMENT) || 
         hasPermission(adminUser, PERMISSIONS.SYSTEM_SETTINGS);
};

// Get current admin user
export const getCurrentAdminUser = () => {
  return sessionStorage.getItem('admin_user');
};

// Logout admin
export const logoutAdmin = () => {
  sessionStorage.removeItem('admin_authenticated');
  sessionStorage.removeItem('admin_user');
};

// Add new admin user (requires proper role)
export const addAdminUser = (username, password, requestingUser) => {
  try {
    // Check if requesting user has permission
    if (!hasPermission(requestingUser, PERMISSIONS.USER_MANAGEMENT)) {
      return false;
    }

    const credentials = getAdminCredentials();
    credentials[username] = password;
    return saveAdminCredentials(credentials);
  } catch (error) {
    console.error('Error adding admin user:', error);
    return false;
  }
};

// Remove admin user (requires proper role)
export const removeAdminUser = (username, requestingUser) => {
  try {
    // Check if requesting user has permission
    if (!hasPermission(requestingUser, PERMISSIONS.USER_MANAGEMENT)) {
      return false;
    }

    // Prevent removing super admin
    const targetRole = getUserRole(username);
    if (targetRole === 'super_admin') {
      return false;
    }

    const credentials = getAdminCredentials();
    delete credentials[username];
    return saveAdminCredentials(credentials);
  } catch (error) {
    console.error('Error removing admin user:', error);
    return false;
  }
};

// Change admin password
export const changeAdminPassword = (username, newPassword, requestingUser) => {
  try {
    // Users can change their own password, or admin can change others
    const canChange = username === requestingUser || hasPermission(requestingUser, PERMISSIONS.USER_MANAGEMENT);
    
    if (!canChange) return false;

    const credentials = getAdminCredentials();
    if (credentials[username]) {
      credentials[username] = newPassword;
      return saveAdminCredentials(credentials);
    }

    return false;
  } catch (error) {
    console.error('Error changing admin password:', error);
    return false;
  }
};

// Create admin backup
export const createAdminBackup = (requestingUser) => {
  try {
    // Check permission
    if (!hasPermission(requestingUser, PERMISSIONS.EXPORT_DATA)) {
      return false;
    }

    const settings = getAdminSettings();
    const leaderboardData = JSON.parse(localStorage.getItem('chess_leaderboard_data') || '{}');
    const rolesData = JSON.parse(localStorage.getItem('chess_user_roles') || '{}');

    const backup = {
      timestamp: Date.now(),
      settings,
      leaderboardData,
      rolesData,
      version: '2.0',
      createdBy: requestingUser
    };

    const backupStr = JSON.stringify(backup, null, 2);
    const dataBlob = new Blob([backupStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `chess_admin_backup_${new Date().toISOString().split('T')[0]}.json`;
    link.click();

    // Update last backup time
    const updatedSettings = { ...settings, lastBackup: Date.now() };
    saveAdminSettings(updatedSettings);

    return true;
  } catch (error) {
    console.error('Error creating admin backup:', error);
    return false;
  }
};

// Restore from backup
export const restoreFromBackup = (backupData, requestingUser) => {
  try {
    // Check permission
    if (!hasPermission(requestingUser, PERMISSIONS.SYSTEM_SETTINGS)) {
      return false;
    }

    if (backupData.settings) {
      saveAdminSettings(backupData.settings);
    }

    if (backupData.leaderboardData) {
      localStorage.setItem('chess_leaderboard_data', JSON.stringify(backupData.leaderboardData));
    }

    if (backupData.rolesData) {
      localStorage.setItem('chess_user_roles', JSON.stringify(backupData.rolesData));
    }

    return true;
  } catch (error) {
    console.error('Error restoring from backup:', error);
    return false;
  }
};

// Initialize admin system with role integration
export const initializeAdminSystem = () => {
  try {
    // Initialize role system first
    initializeRoleSystem();

    // Ensure admin user has super_admin role
    setUserRole('admin', 'super_admin');

    // Ensure moderator user has admin role
    setUserRole('moderator', 'admin');

    // Ensure default settings exist
    const settings = getAdminSettings();
    saveAdminSettings(settings);

    // Ensure default credentials exist
    const credentials = getAdminCredentials();
    saveAdminCredentials(credentials);

    console.log('Admin system initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing admin system:', error);
    return false;
  }
};

// Get system statistics for admin dashboard
export const getSystemStats = () => {
  try {
    const leaderboardData = JSON.parse(localStorage.getItem('chess_leaderboard_data') || '{}');
    const rolesData = JSON.parse(localStorage.getItem('chess_user_roles') || '{}');
    const settings = getAdminSettings();

    const users = Object.values(leaderboardData);
    const totalUsers = users.length;
    const totalGames = users.reduce((sum, user) => sum + (user.gamesPlayed || 0), 0);
    const activeUsers = users.filter(user => 
      user.lastPlayed && (Date.now() - user.lastPlayed) < 7 * 24 * 60 * 60 * 1000
    ).length;

    // Role distribution
    const roleDistribution = {};
    Object.values(rolesData).forEach(roleId => {
      roleDistribution[roleId] = (roleDistribution[roleId] || 0) + 1;
    });

    return {
      totalUsers,
      totalGames,
      activeUsers,
      averageRating: totalUsers > 0 ? Math.round(
        users.reduce((sum, user) => sum + (user.rating || 1200), 0) / totalUsers
      ) : 1200,
      roleDistribution,
      settings,
      lastBackup: settings.lastBackup
    };
  } catch (error) {
    console.error('Error getting system stats:', error);
    return {
      totalUsers: 0,
      totalGames: 0,
      activeUsers: 0,
      averageRating: 1200,
      roleDistribution: {},
      settings: DEFAULT_ADMIN_SETTINGS,
      lastBackup: null
    };
  }
};

// Check if user can access admin panel
export const canAccessAdminPanel = (username) => {
  return hasPermission(username, PERMISSIONS.USER_MANAGEMENT) || 
         hasPermission(username, PERMISSIONS.SYSTEM_SETTINGS) || 
         hasPermission(username, PERMISSIONS.VIEW_ANALYTICS);
};